import React, { useState } from "react";
import { clientsAPI } from "../../../services/api";
import { toast } from "react-toastify";
import { X, Upload } from "lucide-react";
import { handleDuplicateError, formatDuplicateMessage } from '../../../utils/duplicateCheckUtils';

const REQUIRED_FIELDS = {
  companyName:    { section: 'basic',   label: 'Company Name' },
  primaryContact: { section: 'contact', label: 'Primary Contact Name' },
  email:          { section: 'contact', label: 'Email' },
};

const CreateClientModal = ({ onClose, onCreate }) => {
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('basic');
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    // Basic Information
    companyName: "",
    websiteUrl: "",
    industry: "",
    relationshipType: "CLIENT",
    leadStatus: "NEW",
    leadSource: "",
    linkedInUrl: "",
    companyStage: "",
    legalType: "",
    employeeCount: "",
    annualRevenue: "",
    
    // Primary Contact
    primaryContact: "",
    jobTitle: "",
    email: "",
    phone: "",
    contactOwner: "",
    
    // Billing Address
    billingStreet: "",
    billingCity: "",
    billingState: "",
    billingCountry: "",
    billingZipCode: "",
    
    // Shipping Address
    shippingStreet: "",
    shippingCity: "",
    shippingState: "",
    shippingCountry: "",
    shippingZipCode: "",
    sameAsBilling: false,
    
    // Financial Information
    taxIdType: "",
    taxIdNumber: "",
    paymentTerms: "",
    currency: "INR",
    
    // Client Specific
    role: "CLIENT",
    hiringManager: "",
    pocName: "",
    pocJobTitle: "",
    pocEmail: "",
    pocPhone: "",
    
    // Documents
    logo: null,
    primaryAgreement: null,
    workOrder: null,
    supportingDocs: null,
    
    // Preferences
    doNotCall: false,
    doNotEmail: false
  });

  const sections = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'contact', label: 'Contact' },
    { id: 'address', label: 'Address' },
    { id: 'financial', label: 'Financial' },
    { id: 'client', label: 'Client Info' },
    { id: 'documents', label: 'Documents' },
    { id: 'preferences', label: 'Preferences' }
  ];

  const validate = () => {
    const errs = {};
    Object.entries(REQUIRED_FIELDS).forEach(([field, { label }]) => {
      if (!formData[field]?.toString().trim()) errs[field] = `${label} is required`;
    });
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errs.email = 'Please enter a valid email address';
    }
    if (formData.phone) {
      const phoneDigits = formData.phone.replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        errs.phone = 'Please enter a valid 10-digit phone number';
      }
    }
    
    // POC Validation
    if (formData.pocEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.pocEmail)) {
      errs.pocEmail = 'Please enter a valid POC email address';
    }
    if (formData.pocPhone) {
      const pocPhoneDigits = formData.pocPhone.replace(/\D/g, '');
      if (pocPhoneDigits.length < 10) {
        errs.pocPhone = 'Please enter a valid 10-digit POC phone number';
      }
    }
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      const firstField = Object.keys(errs)[0];
      let targetSection = REQUIRED_FIELDS[firstField]?.section || 'basic';
      if (errs.phone) targetSection = 'contact';
      if (errs.pocEmail || errs.pocPhone) targetSection = 'client';
      setActiveSection(targetSection);
      setTimeout(() => {
        const el = document.querySelector(`[name="${firstField}"]`);
        if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus?.(); }
      }, 80);
    }
    return Object.keys(errs).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (errors[name]) setErrors(prev => { const e = { ...prev }; delete e[name]; return e; });
    
    if (type === 'checkbox') {
      setFormData(prev => {
        const newData = { ...prev, [name]: checked };
        
        // If "Same as Billing" is checked, copy billing to shipping
        if (name === 'sameAsBilling' && checked) {
          return {
            ...newData,
            shippingStreet: prev.billingStreet,
            shippingCity: prev.billingCity,
            shippingState: prev.billingState,
            shippingCountry: prev.billingCountry,
            shippingZipCode: prev.billingZipCode
          };
        }
        
        return newData;
      });
    } else if (type === 'file') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleEmailBlur = async (e) => {
    const value = e.target.value.trim();
    if (!value) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return;
    try {
      const res = await clientsAPI.checkEmailExists(value);
      if (res.data?.exists) {
        setErrors(prev => ({ ...prev, email: formatDuplicateMessage('email') }));
      } else {
        setErrors(prev => { const e = { ...prev }; delete e.email; return e; });
      }
    } catch (error) {
      handleDuplicateError(error, (msg) => setErrors(prev => ({ ...prev, email: msg })));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    
    try {
      const formDataToSend = new FormData();
      
      // Append all text fields
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '' && 
            !['logo', 'primaryAgreement', 'workOrder', 'supportingDocs'].includes(key)) {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      // Append files
      if (formData.logo) formDataToSend.append('logo', formData.logo);
      if (formData.primaryAgreement) formDataToSend.append('primaryAgreement', formData.primaryAgreement);
      if (formData.workOrder) formDataToSend.append('workOrder', formData.workOrder);
      if (formData.supportingDocs) formDataToSend.append('supportingDocs', formData.supportingDocs);
      
      await clientsAPI.addClient(formDataToSend);
      toast.success("Client created successfully!");
      onCreate?.();
      onClose();
    } catch (error) {
      console.error("Error creating client:", error);
      toast.error(error.response?.data?.message || "Failed to create client");
    } finally {
      setLoading(false);
    }
  };

  const renderBasicInfo = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Company Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="companyName"
          value={formData.companyName}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent ${errors.companyName ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
        />
        {errors.companyName && <p className="text-red-600 text-xs mt-1 flex items-center gap-1">⚠ {errors.companyName}</p>}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Website URL</label>
          <input
            type="url"
            name="websiteUrl"
            value={formData.websiteUrl}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn URL</label>
          <input
            type="url"
            name="linkedInUrl"
            value={formData.linkedInUrl}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
          <select
            name="industry"
            value={formData.industry}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
          >
            <option value="">Select Industry</option>
            <option value="IT">IT</option>
            <option value="HEALTHCARE">Healthcare</option>
            <option value="FINANCE">Finance</option>
            <option value="EDUCATION">Education</option>
            <option value="RETAIL">Retail</option>
            <option value="MANUFACTURING">Manufacturing</option>
            <option value="OTHER">Other</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Relationship Type</label>
          <select
            name="relationshipType"
            value={formData.relationshipType}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
          >
            <option value="CLIENT">Client</option>
            <option value="VENDOR">Vendor</option>
            <option value="PARTNER">Partner</option>
            <option value="COMPETITOR">Competitor</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Lead Status</label>
          <select
            name="leadStatus"
            value={formData.leadStatus}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
          >
            <option value="NEW">New</option>
            <option value="CONTACTED">Contacted</option>
            <option value="QUALIFIED">Qualified</option>
            <option value="NEGOTIATION">Negotiation</option>
            <option value="WON">Won</option>
            <option value="LOST">Lost</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Lead Source</label>
          <select
            name="leadSource"
            value={formData.leadSource}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
          >
            <option value="">Select Source</option>
            <option value="WEBSITE">Website</option>
            <option value="REFERRAL">Referral</option>
            <option value="SOCIAL_MEDIA">Social Media</option>
            <option value="EMAIL_CAMPAIGN">Email Campaign</option>
            <option value="COLD_CALL">Cold Call</option>
            <option value="EVENT">Event</option>
            <option value="OTHER">Other</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Company Stage</label>
          <select
            name="companyStage"
            value={formData.companyStage}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
          >
            <option value="">Select Stage</option>
            <option value="STARTUP">Startup</option>
            <option value="GROWTH">Growth</option>
            <option value="MATURE">Mature</option>
            <option value="ENTERPRISE">Enterprise</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Legal Type</label>
          <select
            name="legalType"
            value={formData.legalType}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
          >
            <option value="">Select Type</option>
            <option value="SOLE_PROPRIETORSHIP">Sole Proprietorship</option>
            <option value="PARTNERSHIP">Partnership</option>
            <option value="LLC">LLC</option>
            <option value="CORPORATION">Corporation</option>
            <option value="NON_PROFIT">Non-Profit</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Employee Count</label>
          <select
            name="employeeCount"
            value={formData.employeeCount}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
          >
            <option value="">Select Range</option>
            <option value="RANGE_1_10">1-10</option>
            <option value="RANGE_11_50">11-50</option>
            <option value="RANGE_51_100">51-100</option>
            <option value="RANGE_101_250">101-250</option>
            <option value="RANGE_251_500">251-500</option>
            <option value="RANGE_501_1000">501-1000</option>
            <option value="RANGE_1000_PLUS">1000+</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Annual Revenue</label>
        <select
          name="annualRevenue"
          value={formData.annualRevenue}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
        >
          <option value="">Select Range</option>
          <option value="BELOW_1M">Below $1M</option>
          <option value="RANGE_1M_10M">$1M - $10M</option>
          <option value="RANGE_10M_50M">$10M - $50M</option>
          <option value="RANGE_50M_100M">$50M - $100M</option>
          <option value="ABOVE_100M">Above $100M</option>
        </select>
      </div>
    </div>
  );

  const renderContactInfo = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Primary Contact Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="primaryContact"
            value={formData.primaryContact}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent ${errors.primaryContact ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
          />
          {errors.primaryContact && <p className="text-red-600 text-xs mt-1 flex items-center gap-1">⚠ {errors.primaryContact}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
          <input
            type="text"
            name="jobTitle"
            value={formData.jobTitle}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleEmailBlur}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent ${errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
          />
          {errors.email && <p className="text-red-600 text-xs mt-1 flex items-center gap-1">⚠ {errors.email}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent ${errors.phone ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
          />
          {errors.phone && <p className="text-red-600 text-xs mt-1 flex items-center gap-1">⚠ {errors.phone}</p>}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Contact Owner</label>
        <input
          type="text"
          name="contactOwner"
          value={formData.contactOwner}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
        />
      </div>
    </div>
  );

  const renderAddressInfo = () => (
    <div className="space-y-6">
      {/* Billing Address */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Billing Address</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
            <input
              type="text"
              name="billingStreet"
              value={formData.billingStreet}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input
                type="text"
                name="billingCity"
                value={formData.billingCity}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State/Province</label>
              <input
                type="text"
                name="billingState"
                value={formData.billingState}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <input
                type="text"
                name="billingCountry"
                value={formData.billingCountry}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Zip/Postal Code</label>
              <input
                type="text"
                name="billingZipCode"
                value={formData.billingZipCode}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Same as Billing Checkbox */}
      <div className="flex items-center">
        <input
          type="checkbox"
          name="sameAsBilling"
          checked={formData.sameAsBilling}
          onChange={handleChange}
          className="rounded border-gray-300 text-[#1C4E6D] focus:ring-[#1C4E6D]"
        />
        <label className="ml-2 text-sm text-gray-700">
          Shipping address same as billing
        </label>
      </div>

      {/* Shipping Address */}
      {!formData.sameAsBilling && (
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Shipping Address</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
              <input
                type="text"
                name="shippingStreet"
                value={formData.shippingStreet}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input
                  type="text"
                  name="shippingCity"
                  value={formData.shippingCity}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State/Province</label>
                <input
                  type="text"
                  name="shippingState"
                  value={formData.shippingState}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <input
                  type="text"
                  name="shippingCountry"
                  value={formData.shippingCountry}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Zip/Postal Code</label>
                <input
                  type="text"
                  name="shippingZipCode"
                  value={formData.shippingZipCode}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderFinancialInfo = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID Type</label>
          <input
            type="text"
            name="taxIdType"
            value={formData.taxIdType}
            onChange={handleChange}
            placeholder="e.g., EIN, GST, VAT"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID Number</label>
          <input
            type="text"
            name="taxIdNumber"
            value={formData.taxIdNumber}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
          <select
            name="paymentTerms"
            value={formData.paymentTerms}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
          >
            <option value="">Select Terms</option>
            <option value="NET15">Net 15</option>
            <option value="NET30">Net 30</option>
            <option value="NET45">Net 45</option>
            <option value="NET60">Net 60</option>
            <option value="IMMEDIATE">Immediate</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
          <select
            name="currency"
            value={formData.currency}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
          >
            <option value="INR">INR - Indian Rupee</option>
            <option value="USD">USD - US Dollar</option>
            <option value="EUR">EUR - Euro</option>
            <option value="GBP">GBP - British Pound</option>
            <option value="AUD">AUD - Australian Dollar</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderClientInfo = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Hiring Manager</label>
          <input
            type="text"
            name="hiringManager"
            value={formData.hiringManager}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
          <select
            name="role"
            value={formData.role}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
          >
            <option value="CLIENT">Client</option>
            <option value="VENDOR">Vendor</option>
          </select>
        </div>
      </div>

      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Point of Contact (POC)</h4>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">POC Name</label>
              <input
                type="text"
                name="pocName"
                value={formData.pocName}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">POC Job Title</label>
              <input
                type="text"
                name="pocJobTitle"
                value={formData.pocJobTitle}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">POC Email</label>
              <input
                type="email"
                name="pocEmail"
                value={formData.pocEmail}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent ${errors.pocEmail ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
              />
              {errors.pocEmail && <p className="text-red-600 text-xs mt-1 flex items-center gap-1">⚠ {errors.pocEmail}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">POC Phone</label>
              <input
                type="tel"
                name="pocPhone"
                value={formData.pocPhone}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent ${errors.pocPhone ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
              />
              {errors.pocPhone && <p className="text-red-600 text-xs mt-1 flex items-center gap-1">⚠ {errors.pocPhone}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDocuments = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Company Logo</label>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <Upload className="mx-auto text-gray-400 mb-2" size={32} />
          <input
            type="file"
            name="logo"
            onChange={handleChange}
            accept="image/*"
            className="hidden"
            id="logo-upload"
          />
          <label htmlFor="logo-upload" className="cursor-pointer">
            <span className="text-sm text-[#1C4E6D] hover:text-[#153b52]">
              {formData.logo ? formData.logo.name : 'Click to upload logo'}
            </span>
          </label>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Primary Agreement</label>
        <input
          type="file"
          name="primaryAgreement"
          onChange={handleChange}
          accept=".pdf,.doc,.docx"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
        />
        {formData.primaryAgreement && (
          <p className="text-sm text-gray-500 mt-1">{formData.primaryAgreement.name}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Work Order</label>
        <input
          type="file"
          name="workOrder"
          onChange={handleChange}
          accept=".pdf,.doc,.docx"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
        />
        {formData.workOrder && (
          <p className="text-sm text-gray-500 mt-1">{formData.workOrder.name}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Supporting Documents</label>
        <input
          type="file"
          name="supportingDocs"
          onChange={handleChange}
          accept=".pdf,.doc,.docx,.zip"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent"
        />
        {formData.supportingDocs && (
          <p className="text-sm text-gray-500 mt-1">{formData.supportingDocs.name}</p>
        )}
      </div>
    </div>
  );

  const renderPreferences = () => (
    <div className="space-y-4">
      <div className="flex items-center">
        <input
          type="checkbox"
          name="doNotCall"
          checked={formData.doNotCall}
          onChange={handleChange}
          className="rounded border-gray-300 text-[#1C4E6D] focus:ring-[#1C4E6D]"
        />
        <label className="ml-2 text-sm text-gray-700">
          Do Not Call - Client prefers not to be contacted via phone
        </label>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          name="doNotEmail"
          checked={formData.doNotEmail}
          onChange={handleChange}
          className="rounded border-gray-300 text-[#1C4E6D] focus:ring-[#1C4E6D]"
        />
        <label className="ml-2 text-sm text-gray-700">
          Do Not Email - Client prefers not to receive emails
        </label>
      </div>
    </div>
  );

  const renderSection = () => {
    switch (activeSection) {
      case 'basic': return renderBasicInfo();
      case 'contact': return renderContactInfo();
      case 'address': return renderAddressInfo();
      case 'financial': return renderFinancialInfo();
      case 'client': return renderClientInfo();
      case 'documents': return renderDocuments();
      case 'preferences': return renderPreferences();
      default: return renderBasicInfo();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm">
      <div className="bg-white shadow-2xl w-full max-w-3xl h-full flex flex-col animate-slideInRight overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Create Client</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X size={24} />
          </button>
        </div>

        {/* Section Navigation */}
        <div className="flex gap-2 px-6 py-3 border-b bg-gray-50 overflow-x-auto flex-shrink-0">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition ${
                activeSection === section.id
                  ? 'bg-[#1C4E6D] text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {section.label}
            </button>
          ))}
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {renderSection()}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 bg-[#1C4E6D] text-white rounded-lg hover:bg-[#153b52] text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Creating...
                </>
              ) : (
                'Create Client'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateClientModal;
